/**
 * Dinero del catálogo.
 *
 * Los precios se guardan en **centavos, como entero**. Nunca float: `0.1 + 0.2`
 * no es `0.3` y un catálogo de perfumes no es lugar para descubrirlo.
 *
 * La dueña escribe pesos en el panel ("1500", "1,500", "$1500.50") y acá se
 * convierte en un solo lugar. El sitio público hace el camino inverso.
 *
 * Sigue el patrón de `lib/imagen.js`: los errores son `ErrorPrecio` con mensaje
 * en español listo para mostrarle a ella, no códigos para el programador.
 */

/** Tope defensivo: $100,000 MXN. Un perfume no cuesta eso; si alguien escribe
 *  más, es un dedazo (un cero de más) y conviene frenarlo antes de publicarlo. */
export const MAX_CENTAVOS = 100_000 * 100;

export class ErrorPrecio extends Error {}

/**
 * Texto en pesos -> centavos enteros.
 * Vacío devuelve `null`, que significa "sin precio" (la card cae a WhatsApp).
 */
export function aCentavos(texto) {
  if (texto === null || texto === undefined) return null;
  const limpio = String(texto).replace(/[$\s,]/g, '');
  if (!limpio) return null;

  if (!/^\d+(\.\d{1,2})?$/.test(limpio)) {
    throw new ErrorPrecio('El precio solo puede llevar números, por ejemplo 1500 o 1500.50');
  }
  // Se multiplica sobre el texto y no sobre el float para que "0.07" no caiga en
  // 6.999999999999999 y termine truncado a 6 centavos.
  const [enteros, decimales = ''] = limpio.split('.');
  const centavos = Number(enteros) * 100 + Number(decimales.padEnd(2, '0'));

  if (centavos <= 0) throw new ErrorPrecio('El precio tiene que ser mayor a cero.');
  if (centavos > MAX_CENTAVOS) {
    throw new ErrorPrecio(`El precio no puede pasar de $${MAX_CENTAVOS / 100} MXN. ¿Sobra un cero?`);
  }
  return centavos;
}

/** Centavos -> lo que se escribe en el input del panel ("1500", "1500.50"). */
export function aPesos(centavos) {
  if (centavos === null || centavos === undefined) return '';
  return centavos % 100 === 0 ? String(centavos / 100) : (centavos / 100).toFixed(2);
}

const fmt = (decimales) =>
  new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    minimumFractionDigits: decimales,
    maximumFractionDigits: decimales,
  });

/**
 * Para el panel: "$1,500". Sin decimales cuando son .00, porque una lista de
 * precios redondos llena de ",00" se lee peor.
 */
export function formatearCorto(centavos) {
  if (centavos === null || centavos === undefined) return '';
  return fmt(centavos % 100 === 0 ? 0 : 2).format(centavos / 100);
}

/**
 * Para el sitio público: "$1,500 MXN".
 * El "MXN" es explícito a propósito: los perfumes de importación se cotizan en
 * dólares seguido, y un "$1,500" ambiguo en una tienda de lujo cuesta ventas.
 */
export function formatear(centavos) {
  if (centavos === null || centavos === undefined) return '';
  return `${formatearCorto(centavos)} MXN`;
}

/** Texto -> entero de piezas. Vacío es `null` = "no controla inventario". */
export function aPiezas(texto) {
  if (texto === null || texto === undefined) return null;
  const limpio = String(texto).replace(/[\s,]/g, '');
  if (!limpio) return null;
  if (!/^\d+$/.test(limpio)) {
    throw new ErrorPrecio('Las piezas se escriben con números enteros, por ejemplo 3.');
  }
  const n = Number(limpio);
  if (n > 9999) throw new ErrorPrecio('Máximo 9999 piezas.');
  return n;
}
